import React, { useState, useRef, useEffect } from "react";
import styles from "./BulkUpload.module.css";
import {
  getExcelTasksById,
  uploadExcelFile,
  uploadSeriesFile,
  getSeriesImportTasks,
} from "../../api/bulk-upload-api";
import { showToast } from "../../utils/Toastify/Toastify";
import { formatBytes } from "../../utils/helperFunctions/formatBytes";
import { formatDate } from "../../utils/dateFormatter";
import { useExcelPolling } from "../../hooks/useExcelPolling";
import Loader from "../../utils/loader/Loader";
import Button from "../../component/button/Button";
import Pagination from "../pagination/Pagination";
import useExcelListPolling from "../../hooks/useExcelListPolling";

// Generic single-item bulk uploads store original_data as a flat row with a
// `title` field. Series/Season/Episode imports (upload_type "series_excel")
// store it nested instead — { sheet, row_index, row: {...columns} } — with
// the actual title under a different column name per sheet.
const getTaskName = (task) => {
  const data = task?.original_data;
  if (!data) return "";
  if (data.title !== undefined) return data.title || "";
  const row = data.row || {};
  if (data.sheet === "Series") return row["Title"] || "";
  if (data.sheet === "Season") return row["Season Title"] || row["Series Title"] || "";
  if (data.sheet === "Episode") return row["Episode Title"] || "";
  return "";
};

const CATEGORY_CARDS = [
  { label: "Movies, Podcasts & Short Clips" },
  { label: "Series" },
];

const BulkUpload = ({ polling }) => {
  // ✅ FIX: One file input ref per category, keyed by label
  const fileInputRefs = useRef({});

  // ✅ FIX: Per-category selected file map  { [label]: File | null }
  const [selectedFiles, setSelectedFiles] = useState({});

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedExcel, setSelectedExcel] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [activeDragCategory, setActiveDragCategory] = useState(null);
  const [excelList, setExcelList] = useState([]);
  const [pollingExcelId, setPollingExcelId] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [seriesProgress, setSeriesProgress] = useState(null);
  const [seriesIds, setSeriesIds] = useState([]);

  const { getExcelList } = useExcelListPolling(
    currentPage,
    rowsPerPage,
    setTotalCount,
    setExcelList,
    polling
  );

  const totalPages = Math.ceil(
    (totalCount || excelList[0]?.totalCount) / rowsPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setRowsPerPage(pageSize);
  };

  useEffect(() => {
    if (handlePageChange) {
      handlePageChange(currentPage, rowsPerPage);
    }
  }, [currentPage, rowsPerPage]);

  useExcelPolling(pollingExcelId, setSelectedExcel, excelList);

  // ✅ FIX: Handle file change per category
  const handleFileChange = (event, categoryLabel) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles((prev) => ({ ...prev, [categoryLabel]: file }));
    }
  };

  // ✅ FIX: Handle drop per category — sets only that category's file
  const handleDrop = (event, categoryLabel) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    setActiveDragCategory(null);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFiles((prev) => ({ ...prev, [categoryLabel]: file }));
    }
  };
  const handleRemoveFile = (categoryLabel) => {
    setSelectedFiles((prev) => ({ ...prev, [categoryLabel]: null }));
    if (fileInputRefs.current[categoryLabel]) {
      fileInputRefs.current[categoryLabel].value = "";
    }
  };

  const handleDrag = (event, categoryLabel) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
      setActiveDragCategory(categoryLabel);
    } else if (event.type === "dragleave") {
      // ✅ FIX: Only clear when truly leaving the drop zone,
      // not when hovering over a child element inside it
      const relatedTarget = event.relatedTarget;
      if (!event.currentTarget.contains(relatedTarget)) {
        setDragActive(false);
        setActiveDragCategory(null);
      }
    }
  };

  // ✅ FIX: Click opens file input for that specific category only
  const handleCardClick = (categoryLabel) => {
    if (fileInputRefs.current[categoryLabel]) {
      fileInputRefs.current[categoryLabel].value = "";
      fileInputRefs.current[categoryLabel].click();
    }
  };

  const handleUpload = async (categoryLabel) => {
    const file = selectedFiles[categoryLabel];
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      let response;
      if (categoryLabel === "Series") {
        response = await uploadSeriesFile(formData);
      } else {
        response = await uploadExcelFile(formData);
      }
      showToast(response.data.message, "success");

      // ✅ Clear only this category's file after successful upload
      setSelectedFiles((prev) => ({ ...prev, [categoryLabel]: null }));
      if (fileInputRefs.current[categoryLabel]) {
        fileInputRefs.current[categoryLabel].value = "";
      }
      await getExcelList();
    } catch (error) {
      showToast("Upload Failed", "error");
      console.error("Upload error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getExcelTasks = async (id) => {
    setLoading(true);
    setShowMore(true);
    try {
      let response;
      const selected = excelList.find((f) => f.id === id);
      if (selected?.upload_type === "series_excel") {
        response = await getSeriesImportTasks(id);
      } else {
        response = await getExcelTasksById(id);
      }
      setTasks(response?.data?.data);
    } catch (error) {
      showToast(error?.response?.data?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedExcel(null);
    setPollingExcelId(null);
    setSeriesProgress(null);
  };

  return (
    <>
      {loading ? <Loader /> : null}
      <div className={styles.container}>
        <div className={styles.uploadPanel}>
          <div className={styles.categoryCards}>
            {CATEGORY_CARDS.map((category) => (
              <div key={category.label} className={styles.categoryCard}>
                {/* ✅ FIX: Separate hidden input per category */}
                <input
                  ref={(el) => (fileInputRefs.current[category.label] = el)}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleFileChange(e, category.label)}
                  className={styles.fileInput}
                />

                <h3 className={styles.categoryTitle}>{category.label}</h3>
                <div
                  className={`${styles.dropZone} ${activeDragCategory === category.label
                    ? styles.dropZoneActive
                    : ""
                    }`}
                  onDragEnter={(e) => handleDrag(e, category.label)}
                  onDragOver={(e) => handleDrag(e, category.label)}
                  onDragLeave={(e) => handleDrag(e, category.label)}
                  onDrop={(e) => handleDrop(e, category.label)}
                  onClick={() => handleCardClick(category.label)}
                >
                  <p className={styles.dropZoneText}>
                    {activeDragCategory === category.label
                      ? "Drop the Excel file here"
                      : "Drag & Drop Your Excel File Or Click To Select"}
                  </p>
                </div>

                {/* ✅ FIX: Show preview & upload button only for this category's file */}
                {selectedFiles[category.label] && (
                  <div className={styles.filePreview}>
                    <p className={styles.fileName} title={selectedFiles[category.label].name}>
                      Selected: {selectedFiles[category.label].name.length > 30
                        ? selectedFiles[category.label].name.slice(0, 30) + "..."
                        : selectedFiles[category.label].name}
                    </p>
                    <div className={styles.fileActions}>
                      <Button className={styles.actionButton} onClick={() => handleUpload(category.label)}>
                        Upload
                      </Button>
                      <Button className={styles.actionButton} onClick={() => handleRemoveFile(category.label)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={styles.recentUploads}>
            <h3 className={styles.subtitle}>Recent Uploads</h3>
            {excelList?.length === 0 ? (
              <p className={styles.emptyState}>No recent uploads.</p>
            ) : (
              <div className={styles.recentList}>
                {excelList?.slice(0, 3).map((file) => (
                  <span
                    key={file.id}
                    onClick={() => setPollingExcelId(file.id)}
                    className={`${styles.recentItem} ${selectedExcel?.id === file.id
                      ? styles.recentItemActive
                      : ""
                      }`}
                  >
                    <div>
                      <span
                        className={styles.recentFileName}
                        title={file.original_filename}
                      >
                        {file.original_filename}
                      </span>
                      <span className={styles.recentFileTime}>
                        {formatDate(file.created_at)}
                      </span>
                    </div>
                    <span className={styles.recentFileStatus}>
                      {file.uploading > 0 ? "Uploading" : "Completed"}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.detailsPanel}>
          <div className={styles.detailsContainer}>
            <div className={styles.detailsCard}>
              <div className={styles.header}>
                <h2 className={styles.title}>Uploaded Excel Files</h2>
                {selectedExcel && (
                  <div className={styles.headerActions}>
                    <button onClick={handleBack} className={styles.backButton}>
                      ← Back
                    </button>
                  </div>
                )}
              </div>
              {excelList?.length === 0 ? (
                <p className={styles.emptyState}>No files uploaded yet.</p>
              ) : !selectedExcel ? (
                <div className={styles.fileList}>
                  {excelList?.map((file) => (
                    <span
                      key={file.id}
                      onClick={() => setPollingExcelId(file.id)}
                      className={`${styles.fileItem} ${selectedExcel?.id === file.id
                        ? styles.fileItemActive
                        : ""
                        }`}
                    >
                      <div className={styles.fileItemContent}>
                        <span
                          className={`${styles.statusDot} ${file.status === "completed"
                            ? styles.completedDot
                            : styles.uploadingDot
                            }`}
                        ></span>
                        <span title={file.original_filename}>{file.original_filename}</span>
                      </div>
                      <span className={styles.fileStatus}>
                        {formatBytes(file.file_size_bytes) + " " + file.status}
                      </span>
                    </span>
                  ))}
                </div>
              ) : (
                <div className={styles.detailsSection}>
                  <h3 className={styles.subtitle}>
                    Details: {selectedExcel.original_filename}
                  </h3>
                  <div className={styles.summary}>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Total:</span>
                      <span className={styles.summaryValue}>
                        {selectedExcel?.episodes_total ??
                          selectedExcel?.total_records}
                      </span>
                    </div>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Uploaded:</span>
                      <span className={styles.summaryValue}>
                        {selectedExcel?.successful_total ??
                          selectedExcel?.successful_uploads}
                      </span>
                    </div>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Rejected:</span>
                      <span className={styles.summaryValue}>
                        {selectedExcel?.failed_total ??
                          selectedExcel?.failed_uploads}
                      </span>
                    </div>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>Uploading:</span>
                      <span className={styles.summaryValue}>
                        {selectedExcel?.processed_total ??
                          selectedExcel?.processed_records}
                      </span>
                    </div>
                  </div>
                  <h3
                    onClick={() => getExcelTasks(pollingExcelId)}
                    className={styles.showMore}
                  >
                    More Details...
                  </h3>
                  {showMore && (
                    <>
                      <h4 className={styles.subtitle}>Video Status</h4>
                      <div className={styles.videoTable}>
                        <div className={styles.tableHeader}>
                          <span>#</span>
                          <span>Name</span>
                          <span>File Size</span>
                          <span>Upload Time</span>
                          <span>Status</span>
                        </div>
                        {tasks?.length === 0 ? (
                          <p className={styles.emptyState}>
                            No videos available.
                          </p>
                        ) : (
                          <div className={styles.videoList}>
                            {tasks?.map((video) => (
                              <span key={video.id} className={styles.videoItem}>
                                <span>{video.row_number}</span>
                                <span className={styles.videoName}>
                                  {video.original_data?.sheet && (
                                    <span className={styles.videoSheet}>{video.original_data.sheet}</span>
                                  )}
                                  {getTaskName(video) || "—"}
                                </span>
                                <span>
                                  {formatBytes(video.uploaded_file_size)}
                                </span>
                                <span>{formatDate(video.created_at)}</span>
                                <span
                                  className={`${styles.videoStatus} ${styles[(video.status || "").toLowerCase()] || ""}`}
                                >
                                  {video.status === "Uploaded" && (
                                    <span className={styles.statusIcon}>✔</span>
                                  )}
                                  {video.status === "Pending" && (
                                    <span className={styles.statusIcon}>⏳</span>
                                  )}
                                  {video.status === "Rejected" && (
                                    <span className={styles.statusIcon}>✖</span>
                                  )}
                                  {video.status === "Uploading" && (
                                    <span className={styles.loadingDots}>
                                      <span></span>
                                      <span></span>
                                      <span></span>
                                    </span>
                                  )}
                                  {video.status}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
};

export default BulkUpload;